import React, { useState } from 'react';
import { WebLayout } from 'components/Layout';
import { motion } from 'framer-motion';

import DashboardAndIntegrationsUIImg from 'assets/dashboard-and-integrations.png';
import CustomersUIImg from 'assets/customers.png';
import ProductsUIImg from 'assets/products.png';

const usdToUgxRate = 3626; // approximate live rate as of Nov 2025
const toUGX = (usd) => Math.round((usd * usdToUgxRate) / 50) * 50; // round to nearest 50 UGX

const screenshots = [
  {
    id: 'dashboard',
    label: 'Dashboard & Integrations',
    description:
      'Monitor performance, see upcoming tasks, and manage integrations in one control center built around your brand.',
    image: DashboardAndIntegrationsUIImg,
  },
  {
    id: 'products',
    label: 'Products & Inventory',
    description:
      'Launch, update, and organize your products with bulk actions, rich media, and smart automation.',
    image: ProductsUIImg,
  },
  {
    id: 'customers',
    label: 'Customers & Relationships',
    description:
      'Understand your customers with timelines, activity, and notes that your whole team can work from.',
    image: CustomersUIImg,
  },
];

const pricing = [
  {
    id: 'wix',
    name: 'Wix Business',
    priceUsd: 17,
    description: 'Template-based site builder with limited deep customization.',
    bullets: [
      'Starts from $17/month',
      'Template & app-store driven',
      'Feature limits tied to plan tier',
    ],
  },
  {
    id: 'wordpress',
    name: 'WordPress Business',
    priceUsd: 25,
    description: 'Plugin-heavy CMS that needs active management and maintenance.',
    bullets: [
      'Starts from $25/month',
      'Plugin & theme maintenance',
      'Security & updates handled by you/your dev',
    ],
  },
  {
    id: 'ashita',
    name: 'Ashita Custom CMS',
    priceUsd: 10,
    highlight: true,
    description:
      'A fully managed, custom-shaped CMS where you can request new features at no extra cost.',
    bullets: [
      'Starts from $10/month',
      'We customize your CMS to your exact workflows',
      'You suggest new features, we build & ship',
      'No extra feature fees – just your subscription',
    ],
  },
];

export const Console = () => {
  const [activeScreenshotId, setActiveScreenshotId] = useState('dashboard');

  const activeScreenshot = screenshots.find((s) => s.id === activeScreenshotId) || screenshots[0];

  return (
    <WebLayout>
      <div className="relative overflow-hidden">
        {/* Soft gradient background accents */}
        <div className="pointer-events-none absolute inset-0 -z-10">
          <div className="absolute -top-32 -left-32 h-72 w-72 rounded-full bg-[#F9A200]/10 blur-3xl" />
          <div className="absolute -bottom-40 -right-40 h-96 w-96 rounded-full bg-indigo-500/10 blur-3xl" />
        </div>

        {/* HERO */}
        <section className="px-6 pt-10 pb-16 md:px-10 lg:px-20">
          <div className="mx-auto flex max-w-6xl flex-col gap-10 lg:flex-row lg:items-center">
            {/* Text */}
            <motion.div
              className="flex-1"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 0.6, ease: 'easeOut' }}
            >
              <div className="inline-flex items-center gap-2 rounded-full border border-white/10 bg-black/80 px-3 py-1 text-xs uppercase tracking-[0.18em] text-[#F9A200] shadow-lg">
                <span className="h-1.5 w-1.5 rounded-full bg-[#F9A200]" />
                Fully managed • Custom built • Subscription only
              </div>

              <h1 className="mt-5 text-3xl leading-tight text-slate-900 dark:text-white md:text-5xl lg:text-6xl league-spartan-extra-bold">
                Ashita&apos;s{' '}
                <span className="bg-gradient-to-r from-[#F9A200] via-pink-500 to-indigo-500 bg-clip-text text-transparent">
                  Custom CMS
                </span>{' '}
                Services
              </h1>

              <p className="mt-4 max-w-xl text-base text-slate-600 dark:text-slate-200 md:text-lg">
                Get a <strong>custom CMS</strong> that feels like it was built just for your team
                because it is. You register the domain, give us access, and we design, develop,
                maintain, and evolve everything for just{' '}
                <span className="font-semibold">$10/month</span>.
              </p>

              <p className="mt-3 max-w-xl text-sm text-slate-500 dark:text-slate-300">
                Request new features anytime. We add them to your CMS, ship improvements, and never
                charge you per feature. Your only cost: the monthly subscription.
              </p>

              <div className="mt-6 flex flex-wrap items-center gap-4">
                <a
                  href="https://wa.me/256782692271"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="rounded-full bg-[#F9A200] px-6 py-2.5 text-sm font-semibold uppercase tracking-wide text-black shadow-lg shadow-[#F9A200]/40 transition-transform duration-200 hover:-translate-y-0.5 hover:shadow-xl"
                >
                  Start your custom CMS
                </a>
              </div>

              <div className="mt-6 flex flex-wrap items-center gap-4 text-xs text-slate-500 dark:text-slate-300">
                <div className="flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-400" />
                  Zero feature fees – ever
                </div>
                <div className="flex items-center gap-2">
                  <span className="h-1.5 w-1.5 rounded-full bg-sky-400" />
                  Built, hosted & maintained for you
                </div>
              </div>
            </motion.div>

            {/* Screenshot showcase */}
            <motion.div
              className="flex-1"
              initial={{ opacity: 0, x: 40 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true, amount: 0.4 }}
              transition={{ duration: 0.6, ease: 'easeOut', delay: 0.1 }}
            >
              <div className="rounded-3xl border border-slate-200/40 bg-white/80 p-3 shadow-2xl backdrop-blur dark:border-slate-700 dark:bg-slate-900/80">
                {/* Tabs */}
                <div className="flex flex-col md:flex-row gap-1 rounded-2xl bg-slate-100/70 p-1 text-xs dark:bg-slate-800/80">
                  {screenshots.map((s) => {
                    const active = s.id === activeScreenshotId;
                    return (
                      <button
                        key={s.id}
                        onClick={() => setActiveScreenshotId(s.id)}
                        className={`flex-1 rounded-xl px-3 py-2 text-[11px] font-semibold transition-all ${
                          active
                            ? 'bg-white text-slate-900 shadow-sm dark:bg-slate-900 dark:text-white'
                            : 'text-slate-500 hover:text-slate-800 dark:text-slate-400 dark:hover:text-slate-100'
                        }`}
                      >
                        {s.label}
                      </button>
                    );
                  })}
                </div>

                {/* Image + description */}
                <div className="mt-4 grid gap-4 md:grid-cols-[minmax(0,1.3fr)_minmax(0,1fr)]">
                  <motion.div
                    key={activeScreenshot.id}
                    initial={{ opacity: 0, y: 15, scale: 0.99 }}
                    animate={{ opacity: 1, y: 0, scale: 1 }}
                    transition={{ duration: 0.35, ease: 'easeOut' }}
                    className="relative overflow-hidden rounded-2xl border border-slate-200/70 bg-slate-950/80 p-1 shadow-xl dark:border-slate-700"
                  >
                    <div className="mb-2 flex items-center gap-1 px-2 pt-2">
                      <span className="h-2 w-2 rounded-full bg-red-400" />
                      <span className="h-2 w-2 rounded-full bg-amber-300" />
                      <span className="h-2 w-2 rounded-full bg-emerald-400" />
                    </div>
                    <div className="overflow-hidden rounded-xl">
                      <img
                        src={activeScreenshot.image}
                        alt={activeScreenshot.label}
                        className="max-h-80 w-full object-cover transition-transform duration-500 hover:scale-[1.03]"
                      />
                    </div>
                    <div className="pointer-events-none absolute inset-0 rounded-2xl ring-1 ring-white/5" />
                  </motion.div>

                  <motion.div
                    key={`${activeScreenshot.id}-text`}
                    initial={{ opacity: 0, y: 10 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.3, ease: 'easeOut', delay: 0.05 }}
                    className="flex flex-col justify-between gap-3 text-sm"
                  >
                    <div>
                      <div className="text-xs font-semibold uppercase tracking-[0.18em] text-[#F9A200]">
                        {activeScreenshot.label}
                      </div>
                      <p className="mt-2 text-slate-700 dark:text-slate-200">
                        {activeScreenshot.description}
                      </p>
                    </div>
                    <ul className="space-y-2 text-xs text-slate-500 dark:text-slate-300">
                      <li className="flex items-start gap-2">
                        <span className="mt-1 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[#F9A200]" />
                        <span>
                          Designed, configured, and maintained by Ashita so you don&apos;t have to
                          worry about plugins or updates.
                        </span>
                      </li>
                      <li className="flex items-start gap-2">
                        <span className="mt-1 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-indigo-500" />
                        <span>
                          Built for teams that want a CMS that matches <strong>their</strong>{' '}
                          workflows, not a generic template.
                        </span>
                      </li>
                    </ul>
                  </motion.div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* HOW IT WORKS */}
        <section className="px-6 pb-16 md:px-10 lg:px-20">
          <motion.div
            className="mx-auto max-w-5xl rounded-3xl border border-slate-200/60 bg-white/80 p-6 shadow-xl backdrop-blur dark:border-slate-700 dark:bg-slate-900/80"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          >
            <div className="flex flex-col gap-6 md:flex-row md:items-start">
              <div className="md:w-1/3">
                <h2 className="text-xl font-bold text-slate-900 dark:text-white md:text-2xl">
                  A CMS that grows with you
                </h2>
                <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
                  We don&apos;t just hand you a dashboard. We partner with you to shape it around
                  your exact processes — and keep improving it as your needs evolve.
                </p>
              </div>
              <div className="grid flex-1 gap-4 md:grid-cols-3">
                {[
                  {
                    step: '1',
                    title: 'You bring the domain',
                    body: 'You register your domain and give us access. No infrastructure headaches, no setup chaos.',
                  },
                  {
                    step: '2',
                    title: 'We build & customize',
                    body: 'We design and deploy your CMS, wire it to your flows, and tailor it to your team’s language and structure.',
                  },
                  {
                    step: '3',
                    title: 'You request new features',
                    body: 'Need a new report, block, or workflow? Suggest it from within the CMS. We ship it at no extra feature cost.',
                  },
                ].map((item) => (
                  <div
                    key={item.step}
                    className="group relative overflow-hidden rounded-2xl border border-slate-200/60 bg-slate-50/70 p-4 text-sm shadow-sm transition-transform duration-200 hover:-translate-y-1 hover:shadow-lg dark:border-slate-700 dark:bg-slate-900"
                  >
                    <div className="absolute inset-0 bg-gradient-to-br from-[#F9A200]/0 via-[#F9A200]/0 to-indigo-500/0 opacity-0 transition-opacity duration-300 group-hover:opacity-10" />
                    <div className="mb-2 inline-flex h-7 w-7 items-center justify-center rounded-full bg-black text-xs font-semibold text-[#F9A200] dark:bg-slate-800">
                      {item.step}
                    </div>
                    <h3 className="text-sm font-semibold text-slate-900 dark:text-white">
                      {item.title}
                    </h3>
                    <p className="mt-1 text-xs text-slate-600 dark:text-slate-300">{item.body}</p>
                  </div>
                ))}
              </div>
            </div>
          </motion.div>
        </section>

        {/* PRICING COMPARISON */}
        <section className="px-6 pb-18 md:px-10 lg:px-20">
          <motion.div
            className="mx-auto max-w-6xl"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          >
            <div className="flex flex-col gap-3 text-center">
              <div className="mx-auto inline-flex items-center gap-2 rounded-full bg-black px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.2em] text-[#F9A200]">
                CMS Pricing & Comparisons
              </div>
              <h2 className="text-2xl font-bold text-slate-900 dark:text-white md:text-3xl">
                Pay less than Wix or WordPress — with more flexibility
              </h2>
              <p className="mx-auto max-w-2xl text-sm text-slate-600 dark:text-slate-300">
                Most platforms charge you more as you grow or lock powerful features behind higher
                tiers. With Ashita, you pay less <em>and</em> can request new CMS features without
                extra fees.
              </p>
            </div>

            <div className="mt-8 grid gap-4 md:grid-cols-3">
              {pricing.map((plan) => {
                const isAshita = plan.highlight;
                return (
                  <div
                    key={plan.id}
                    className={`relative flex flex-col rounded-3xl border p-5 text-sm shadow-sm transition-transform duration-200 hover:-translate-y-1 hover:shadow-xl ${
                      isAshita
                        ? 'border-[#F9A200]/60 bg-black text-white'
                        : 'border-slate-200/70 bg-white/80 dark:border-slate-700 dark:bg-slate-900/90'
                    }`}
                  >
                    {isAshita && (
                      <div className="absolute right-4 top-4 rounded-full bg-[#F9A200] px-3 py-1 text-[10px] font-semibold uppercase tracking-[0.2em] text-black">
                        Best value
                      </div>
                    )}

                    <div className="mb-3 text-xs font-semibold uppercase tracking-[0.18em] opacity-70">
                      {plan.name}
                    </div>

                    <div className="mb-1 flex items-baseline gap-1">
                      <span className="text-2xl font-semibold">
                        ${plan.priceUsd}
                        <span className="text-xs font-normal opacity-70">/month</span>
                      </span>
                    </div>
                    <div
                      className={`text-xs ${
                        isAshita
                          ? 'text-[#F9A200] font-semibold'
                          : 'text-slate-500 dark:text-slate-300'
                      }`}
                    >
                      ≈ UGX {toUGX(plan.priceUsd).toLocaleString()}
                      <span
                        className={isAshita ? 'ml-1 opacity-70' : 'ml-1 text-[10px] opacity-70'}
                      >
                        (subject to FX rates)
                      </span>
                    </div>

                    <p
                      className={`mt-3 text-xs ${
                        isAshita ? 'text-slate-100/90' : 'text-slate-600 dark:text-slate-300'
                      }`}
                    >
                      {plan.description}
                    </p>

                    <ul className="mt-4 flex-1 space-y-2 text-xs">
                      {plan.bullets.map((b) => (
                        <li key={b} className="flex items-start gap-2">
                          <span
                            className={`mt-1 h-1.5 w-1.5 flex-shrink-0 rounded-full ${
                              isAshita ? 'bg-[#F9A200]' : 'bg-slate-400 dark:bg-slate-500'
                            }`}
                          />
                          <span>{b}</span>
                        </li>
                      ))}
                    </ul>

                    {isAshita && (
                      <button className="mt-5 w-full rounded-full bg-[#F9A200] py-2 text-xs font-semibold uppercase tracking-[0.16em] text-black shadow-md shadow-[#F9A200]/40 transition-transform duration-150 hover:-translate-y-0.5 hover:shadow-lg">
                        Choose Ashita Custom CMS
                      </button>
                    )}
                  </div>
                );
              })}
            </div>

            <p className="mt-4 text-center text-[11px] text-slate-500 dark:text-slate-400">
              You only pay the monthly subscription. You register the domain and provide access —
              Ashita handles setup, customization, maintenance, and feature rollout.
            </p>
          </motion.div>
        </section>

        {/* ADVANTAGES SECTION */}
        <section className="px-6 pb-20 md:px-10 lg:px-20">
          <motion.div
            className="mx-auto max-w-5xl rounded-3xl border border-slate-200/70 bg-white/90 p-6 shadow-xl backdrop-blur dark:border-slate-700 dark:bg-slate-900/90"
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.3 }}
            transition={{ duration: 0.5, ease: 'easeOut' }}
          >
            <div className="grid gap-6 md:grid-cols-[1.1fr_minmax(0,1fr)]">
              <div>
                <h2 className="text-xl font-bold text-slate-900 dark:text-white md:text-2xl">
                  Why Ashita over Wix &amp; WordPress?
                </h2>
                <p className="mt-2 text-sm text-slate-600 dark:text-slate-300">
                  Traditional CMS platforms give you templates and plugins. Ashita gives you a
                  product team that evolves your CMS with you — without charging for every extra
                  feature you need.
                </p>

                <ul className="mt-4 space-y-3 text-sm text-slate-700 dark:text-slate-200">
                  <li className="flex items-start gap-2">
                    <span className="mt-1 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-[#F9A200]" />
                    <span>
                      <strong>Feature requests built in:</strong> Ask for new modules, blocks, or
                      reports directly from within the CMS. We design, build, and deploy them — no
                      per-feature fees.
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="mt-1 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-indigo-500" />
                    <span>
                      <strong>No plugin chaos:</strong> Forget hunting for plugins and worrying
                      about compatibility. We manage the stack and keep everything fast and stable.
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="mt-1 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-emerald-400" />
                    <span>
                      <strong>One simple relationship:</strong> You register your domain and give us
                      access. We handle CMS architecture, integrations, updates, and UX.
                    </span>
                  </li>
                  <li className="flex items-start gap-2">
                    <span className="mt-1 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-pink-400" />
                    <span>
                      <strong>Aligned incentives:</strong> We only make more money if you stay
                      subscribed. That means we’re motivated to keep improving your CMS experience
                      over time.
                    </span>
                  </li>
                </ul>
              </div>

              <div className="flex flex-col justify-between gap-4">
                <div className="rounded-2xl border border-dashed border-slate-300/80 bg-slate-50/80 p-4 text-xs dark:border-slate-600 dark:bg-slate-900">
                  <div className="text-[11px] font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                    Your commitment
                  </div>
                  <ul className="mt-2 space-y-2 text-slate-600 dark:text-slate-300">
                    <li className="flex items-start gap-2">
                      <span className="mt-1 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-slate-400" />
                      <span>Register your preferred domain name.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-1 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-slate-400" />
                      <span>Give Ashita secure access to configure DNS and hosting.</span>
                    </li>
                    <li className="flex items-start gap-2">
                      <span className="mt-1 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-slate-400" />
                      <span>
                        Maintain your <strong>$10/month</strong> (≈ UGX {toUGX(10).toLocaleString()}
                        ) subscription.
                      </span>
                    </li>
                  </ul>
                </div>

                <div className="rounded-2xl bg-black px-4 py-5 text-xs text-[#F9A200] shadow-lg">
                  <p className="font-semibold uppercase tracking-[0.16em]">
                    No extra feature billing
                  </p>
                  <p className="mt-2 text-[11px] text-slate-100">
                    Whether you request 1 new feature or 20 in a year, you still pay the same
                    subscription. Our goal is to become the CMS team you never had to hire.
                  </p>
                </div>
              </div>
            </div>
          </motion.div>
        </section>
      </div>
    </WebLayout>
  );
};
